import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { setPayslipPublication } from '@/services/compensation.service';
import { COMPENSATION_QUERY_KEY } from './CompensationPanel';

export const PAYSLIP_PUBLICATION_QUERY_KEY = 'payslip-publication';

const MONTHS_SHOWN = 12;

/**
 * The "Payslips" tile of a company, for HLB: AB delivers a payslip before the
 * accountant finished the month, so employees and salary viewers see a month
 * only once it is published here. Publishing a month publishes the older ones
 * too; withdrawing moves back to the month before.
 *
 * @param {Object} props
 * @param {string} props.companyId
 * @param {Object} props.data - Response of GET .../payslip-publication.
 */
export default function PayslipPublicationCard({ companyId, data }) {
  const { t, i18n } = useTranslation('translations');
  const tc = (key, opts) => t(`compensation.${key}`, opts);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (period) => setPayslipPublication(companyId, period),
    onSuccess: (next) => {
      queryClient.setQueryData([PAYSLIP_PUBLICATION_QUERY_KEY, companyId], next);
      queryClient.invalidateQueries({ queryKey: ['company-compensation', companyId] });
      queryClient.invalidateQueries({ queryKey: [COMPENSATION_QUERY_KEY] });
      message.success(tc('saved'));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || tc('saveFailed')),
  });

  const months = data?.months || [];
  const monthName = (period) =>
    dayjs(`${period.slice(0, 4)}-${period.slice(4)}-01`)
      .locale(i18n.language)
      .format('MMMM YYYY');
  // The month a withdrawal falls back to: the next older one AB delivered.
  const newestPublished = months.findIndex((m) => m.published);
  const fallback = months[newestPublished + 1]?.period ?? null;

  return (
    <section className="rounded-md border bg-zinc-50 px-4 py-4">
      <div className="mb-1 text-sm font-bold">{tc('publicationTitle')}</div>
      <div className="mb-3 text-[13px] text-zinc-500">{tc('publicationHint')}</div>
      {data?.publishedBy && data?.publishedAt && (
        <div className="mb-3 text-[13px] text-zinc-500">
          {tc('publicationLastChange', {
            name: data.publishedBy,
            date: dayjs(data.publishedAt).format('DD.MM.YYYY HH:mm'),
          })}
        </div>
      )}
      <div className="grid gap-2">
        {months.slice(0, MONTHS_SHOWN).map((m, index) => (
          <div
            key={m.period}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-white px-3 py-2"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold capitalize">
                  {monthName(m.period)}
                </span>
                <Tag color={m.published ? 'green' : 'orange'}>
                  {tc(m.published ? 'published' : 'notPublished')}
                </Tag>
              </div>
              <div className="text-[13px] text-zinc-500">
                {tc('publicationCounts', {
                  payslips: m.payslips,
                  linked: m.linked,
                })}
              </div>
            </div>
            {!m.published && (
              <Popconfirm
                title={tc('publishConfirm', { month: monthName(m.period) })}
                okText={tc('publish')}
                cancelText={tc('cancel')}
                okButtonProps={{ shape: 'round' }}
                cancelButtonProps={{ shape: 'round' }}
                onConfirm={() => save.mutate(m.period)}
              >
                <Button shape="round" type="primary" loading={save.isPending}>
                  {tc('publish')}
                </Button>
              </Popconfirm>
            )}
            {m.published && index === newestPublished && (
              <Popconfirm
                title={tc('withdrawConfirm', { month: monthName(m.period) })}
                okText={tc('withdraw')}
                cancelText={tc('cancel')}
                okButtonProps={{ shape: 'round', danger: true }}
                cancelButtonProps={{ shape: 'round' }}
                onConfirm={() => save.mutate(fallback)}
              >
                <Button shape="round" loading={save.isPending}>
                  {tc('withdraw')}
                </Button>
              </Popconfirm>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
